import { FgdsProject } from "@domain/project/fgds-project";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import {
  LocalizeCableInletTypePipe,
  LocalizeDoorstopTypePipe,
} from "@domain/project/floorplan/floorplan-gas-warning-center-cabinet";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";

type NotesRowData = {
  position: string;
  notes: string[];
  name?: string;
  text?: string;
  textColor?: string;
  backgroundColor?: string;
};

type NotesSection = {
  rows: NotesRowData[];
  productType: string;
};

type NotesGroup = {
  name: string;
  sections: NotesSection[];
};

type NotesSourceValue =
  | FloorplanGasWarningCenter[]
  | FloorplanTransmitter[]
  | FloorplanFlameDetector[]
  | FloorplanAlarmDevice[]
  | FloorplanSignalElement[]
  | FloorplanPlasticSign[]
  | FloorplanGasWarningCenterPlaceholder[]
  | FloorplanTransmitterPlaceholder[]
  | FloorplanFlameDetectorPlaceholder[]
  | FloorplanSimplePlaceholder[];

type NotesSectionKey =
  | "gasWarningCenters"
  | "transmitters"
  | "flameDetectors"
  | "alarmDevices"
  | "signalElements"
  | "plasticSigns"
  | "gasWarningCenterPlaceholders"
  | "transmitterPlaceholders"
  | "flameDetectorPlaceholders"
  | "alarmDevicePlaceholders"
  | "signalElementPlaceholders"
  | "plasticSignPlaceholders";

export enum ProductTypes {
  GAS_WARNING_CENTER = "gasWarningCenter",
  TRANSMITTER = "transmitter",
  FLAME_DETECTOR = "flameDetector",
  ALARM_DEVICE = "alarmDevice",
  SIGNAL_ELEMENT = "signalElement",
  PLASTICSIGN = "plasticSign",
  GAS_WARNING_CENTER_PLACEHOLDER = "gasWarningCenterPlaceholder",
  TRANSMITTER_PLACEHOLDER = "transmitterPlaceholder",
  FLAME_DETECTOR_PLACEHOLDER = "flameDetectorPlaceholder",
  ALARM_DEVICE_PLACEHOLDER = "alarmDevicePlaceholder",
  SIGNALELEMENT_PLACEHOLDER = "signalElementPlaceholder",
  PLASTICSIGN_PLACEHOLDER = "plasticSignPlaceholder",
}

export class TransmitterAdditionalNotes {
  public readonly RADIUS = $localize`:@@pdfExport.productNotes.transmitter.radius:Radius Überwachungsbereich`;
  public readonly BMK = $localize`:@@pdfExport.productNotes.transmitter.bmk:BMK`;
  public readonly SECTOR = $localize`:@@pdfExport.productNotes.transmitter.sector:Sektor`;
  public readonly INSTALLATION_HEIGHT = $localize`:@@pdfExport.productNotes.transmitter.installationHeight:Montagehöhe`;
  public readonly EX_PROTECTION = $localize`:@@pdfExport.productNotes.transmitter.exProtection:Ex-Schutz`;
}

export class FlameDetectorAdditionalNotes {
  public readonly COVERAGE_AREA = $localize`:@@pdfExport.productNotes.flameDetector.coverageArea:Überwachungsbereich`;
  public readonly SENSITIVITY = $localize`:@@pdfExport.productNotes.flameDetector.sensitivity:Empfindlichkeit`;
  public readonly MOUNTING_HEIGHT = $localize`:@@pdfExport.productNotes.flameDetector.mountingHeight:Montagehöhe`;
}

export class GasWarningCenterCabinetNotes {
  public readonly CABINET = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet:Schaltschrank`;
  public readonly MAX_HEIGHT = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.maxHeight:Maximale Höhe`;
  public readonly MAX_WIDTH = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.maxWidth:Maximale Breite`;
  public readonly MAX_DEPTH = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.maxDepth:Maximale Tiefe`;
  public readonly MATERIAL = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.material:Material`;
  public readonly DOORSTOP = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.doorstop:Türausrichtung`;
  public readonly CABLE_INLET = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.cableInlet:Kabeleingang`;
  public readonly OUTDOOR_INSTALLATION = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.outdoorInstallation:UV-gechützt/Außeninstallation`;
  public readonly MAX_ENVIRONMENTAL_TEMPERATURE = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.maxEnvironmentalTemperature:Höchstumgebungstemperatur`;
  public readonly MIN_ENVIRONMENTAL_TEMPERATURE = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.minEnvironmentalTemperature:Mindestumgebungstemperatur`;
  public readonly IP_PROTECTION_CATEGORY = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.ipProtectionCategory:IP-Sicherheitskategorie`;
  public readonly CABINET_NOTES = $localize`:@@pdfExport.productNotes.gasWarningCenter.cabinet.notes:Schaltschranknotizen`;
}

export class ProductNotes {
  defaultNotesGroup: NotesGroup;
  groups: NotesGroup[] = [];
  _hasNotes: boolean = false;
  readonly transmitterAdditionalNotes = new TransmitterAdditionalNotes();
  readonly flameDetectorAdditionalNotes = new FlameDetectorAdditionalNotes();

  readonly gasWarningCenterCabinetNotes = new GasWarningCenterCabinetNotes();

  public constructor(floorplan: Floorplan, project: FgdsProject) {
    this.defaultNotesGroup = { name: $localize`:@@productList.defaultGroup:Sonstige Produkte`, sections: [] };
    this.collectNotes(floorplan, project);
    this._hasNotes = this.checkForNotes();
  }
  get hasNotes() {
    return this._hasNotes;
  }

  private checkForNotes(): boolean {
    return [this.defaultNotesGroup, ...this.groups].some((group) => group.sections.some((section) => section.rows.length > 0));
  }

  private getNotesFromSection(
    floorplanItems:
      | FloorplanGasWarningCenter[]
      | FloorplanTransmitter[]
      | FloorplanFlameDetector[]
      | FloorplanAlarmDevice[]
      | FloorplanSignalElement[]
      | FloorplanPlasticSign[]
      | FloorplanGasWarningCenterPlaceholder[]
      | FloorplanTransmitterPlaceholder[]
      | FloorplanFlameDetectorPlaceholder[]
      | FloorplanSimplePlaceholder[],
  ): NotesRowData[] {
    return floorplanItems
      .filter((item) => {
        if (item instanceof FloorplanGasWarningCenter || item instanceof FloorplanGasWarningCenterPlaceholder)
          return this.hasGasWarningCenterNotes(item as FloorplanGasWarningCenter | FloorplanGasWarningCenterPlaceholder);
        if (item instanceof FloorplanTransmitter || item instanceof FloorplanTransmitterPlaceholder)
          return this.hasTransmitterNotes(item as FloorplanTransmitter | FloorplanTransmitterPlaceholder);
        if (item instanceof FloorplanFlameDetector || item instanceof FloorplanFlameDetectorPlaceholder)
          return this.hasFlameDetectorNotes(item as FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder);
        if (
          item instanceof FloorplanAlarmDevice ||
          item instanceof FloorplanSignalElement ||
          item instanceof FloorplanPlasticSign ||
          item instanceof FloorplanSimplePlaceholder
        ) {
          return !!item.displayName || item.notes.length > 0;
        }

        return false;
      })
      .map((filteredItem) => {
        let configName = "";
        if (
          filteredItem instanceof FloorplanGasWarningCenter ||
          filteredItem instanceof FloorplanTransmitter ||
          filteredItem instanceof FloorplanFlameDetector ||
          filteredItem instanceof FloorplanAlarmDevice ||
          filteredItem instanceof FloorplanSignalElement ||
          filteredItem instanceof FloorplanPlasticSign
        ) {
          configName = filteredItem.config.name;
        }
        if (filteredItem instanceof FloorplanPlasticSign) {
          return this.getNotesForPlasticSign(filteredItem);
        }
        const notes: string[] = this.getNotes(filteredItem);
        return {
          position: filteredItem.positionId ?? "",
          notes: notes,
          name: filteredItem.displayName ? `${filteredItem.displayName} \n${configName}` : `${configName}`,
        };
      });
  }

  private getNotes(
    item:
      | FloorplanGasWarningCenter
      | FloorplanGasWarningCenterPlaceholder
      | FloorplanTransmitter
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetector
      | FloorplanFlameDetectorPlaceholder
      | FloorplanAlarmDevice
      | FloorplanSignalElement
      | FloorplanPlasticSign
      | FloorplanSimplePlaceholder,
  ) {
    if (item instanceof FloorplanGasWarningCenter) return this.buildGasWarningCenterNotes(item);
    if (item instanceof FloorplanGasWarningCenterPlaceholder) return this.buildGasWarningCenterNotes(item);
    if (item instanceof FloorplanTransmitter) return this.buildTransmitterNotes(item);
    if (item instanceof FloorplanTransmitterPlaceholder) return this.buildTransmitterNotes(item);
    if (item instanceof FloorplanFlameDetector) return this.buildFlameDetectorNotes(item);
    if (item instanceof FloorplanFlameDetectorPlaceholder) return this.buildFlameDetectorNotes(item);
    return [item.notes];
  }

  private getNotesForPlasticSign(item: FloorplanPlasticSign) {
    return {
      position: item.positionId!,
      notes: [item.notes],
      name: item.displayName ? `${item.displayName} \n${item.config.name}` : `${item.config.name}`,
      text: item.config.text,
      textColor: item.config.textColor,
      backgroundColor: item.config.backgroundColor,
    };
  }

  private buildTransmitterNotes(item: FloorplanTransmitter | FloorplanTransmitterPlaceholder) {
    const result = [];

    if (item.notes.length) result.push(item.notes);
    if (item.referenceMonitoringAreaRadius)
      result.push(this.transmitterAdditionalNotes.RADIUS + ": " + item.referenceMonitoringAreaRadius + "m");
    if (item.bmk) result.push(this.transmitterAdditionalNotes.BMK + ": " + item.bmk);
    if (item.sector) result.push(this.transmitterAdditionalNotes.SECTOR + ": " + item.sector);
    if (item.installationHeight)
      result.push(this.transmitterAdditionalNotes.INSTALLATION_HEIGHT + ": " + item.installationHeight);
    if (item.exProtection) result.push(this.transmitterAdditionalNotes.EX_PROTECTION);

    return result;
  }

  private hasTransmitterNotes(transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder): boolean {
    return !!(
      transmitter.referenceMonitoringAreaRadius ||
      transmitter.displayName ||
      transmitter.notes.length > 0 ||
      transmitter.bmk ||
      transmitter.sector ||
      transmitter.installationHeight ||
      transmitter.exProtection
    );
  }

  private buildFlameDetectorNotes(item: FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder) {
    const result = [];

    if (item.notes.length) result.push(item.notes);
    if (item.referenceCoverageAreaLength)
      result.push(this.flameDetectorAdditionalNotes.COVERAGE_AREA + ": " + item.referenceCoverageAreaLength + "m");
    if (item.sensitivity) result.push(this.flameDetectorAdditionalNotes.SENSITIVITY + ": " + item.sensitivity);
    if (item.mountingHeight) result.push(this.flameDetectorAdditionalNotes.MOUNTING_HEIGHT + ": " + item.mountingHeight);

    return result;
  }

  private hasFlameDetectorNotes(flameDetector: FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder): boolean {
    return !!(
      flameDetector.referenceCoverageAreaLength ||
      flameDetector.displayName ||
      flameDetector.notes.length > 0 ||
      flameDetector.sensitivity ||
      flameDetector.mountingHeight
    );
  }

  private buildGasWarningCenterNotes(item: FloorplanGasWarningCenter | FloorplanGasWarningCenterPlaceholder) {
    const result = [];

    const localizeDoorstopType = new LocalizeDoorstopTypePipe();
    const localizeCableInletType = new LocalizeCableInletTypePipe();

    if (item.notes.length) result.push(item.notes);
    if (item.cabinet) {
      const cabinet = item.cabinet;
      result.push(this.gasWarningCenterCabinetNotes.CABINET + ":");
      if (cabinet.maxHeight) result.push(this.gasWarningCenterCabinetNotes.MAX_HEIGHT + ": " + cabinet.maxHeight);
      if (cabinet.maxWidth) result.push(this.gasWarningCenterCabinetNotes.MAX_WIDTH + ": " + cabinet.maxWidth);
      if (cabinet.maxDepth) result.push(this.gasWarningCenterCabinetNotes.MAX_DEPTH + ": " + cabinet.maxDepth);
      if (cabinet.material) result.push(this.gasWarningCenterCabinetNotes.MATERIAL + ": " + cabinet.material);
      if (cabinet.doorstop)
        result.push(this.gasWarningCenterCabinetNotes.DOORSTOP + ": " + localizeDoorstopType.transform(cabinet.doorstop));
      if (cabinet.cableInlet)
        result.push(this.gasWarningCenterCabinetNotes.CABLE_INLET + ": " + localizeCableInletType.transform(cabinet.cableInlet));
      if (cabinet.outdoorInstallation) result.push(this.gasWarningCenterCabinetNotes.OUTDOOR_INSTALLATION);
      if (cabinet.minEnvironmentalTemperature)
        result.push(this.gasWarningCenterCabinetNotes.MIN_ENVIRONMENTAL_TEMPERATURE + ": " + cabinet.minEnvironmentalTemperature);
      if (cabinet.maxEnvironmentalTemperature)
        result.push(this.gasWarningCenterCabinetNotes.MAX_ENVIRONMENTAL_TEMPERATURE + ": " + cabinet.maxEnvironmentalTemperature);
      if (cabinet.ipProtectionCategory)
        result.push(this.gasWarningCenterCabinetNotes.IP_PROTECTION_CATEGORY + ": " + cabinet.ipProtectionCategory);
      if (cabinet.notes) result.push(this.gasWarningCenterCabinetNotes.CABINET_NOTES + ": " + cabinet.notes);
    }
    return result;
  }

  private hasGasWarningCenterNotes(gasWarningCenter: FloorplanGasWarningCenter | FloorplanGasWarningCenterPlaceholder) {
    return (
      gasWarningCenter.displayName ||
      gasWarningCenter.notes.length > 0 ||
      (gasWarningCenter.cabinet &&
        (gasWarningCenter.cabinet.maxHeight ||
          gasWarningCenter.cabinet.maxWidth ||
          gasWarningCenter.cabinet.maxDepth ||
          gasWarningCenter.cabinet.material ||
          gasWarningCenter.cabinet.doorstop ||
          gasWarningCenter.cabinet.cableInlet ||
          gasWarningCenter.cabinet.outdoorInstallation ||
          gasWarningCenter.cabinet.minEnvironmentalTemperature ||
          gasWarningCenter.cabinet.maxEnvironmentalTemperature ||
          gasWarningCenter.cabinet.ipProtectionCategory ||
          gasWarningCenter.cabinet.notes))
    );
  }

  private buildSections(source: Record<NotesSectionKey, NotesSourceValue>): NotesSection[] {
    return (Object.keys(source) as NotesSectionKey[]).map((key) => ({
      productType: this.NOTES_SECTION_KEY_TO_PRODUCT_TYPE[key],
      rows: this.getNotesFromSection(source[key]),
    }));
  }

  private collectNotes(floorplan: Floorplan, project: FgdsProject): void {
    const groupedIds = new Set(
      project.groups.flatMap((g) =>
        g.references.filter((r) => r.floorplanId === floorplan.id).map((r) => r.floorplanProductItemId),
      ),
    );

    const ungroupedSource: Record<NotesSectionKey, NotesSourceValue> = {
      gasWarningCenters: floorplan.gasWarningCenters.filter((d) => !groupedIds.has(d.id)),
      transmitters: floorplan.transmitters.filter((d) => !groupedIds.has(d.id)),
      flameDetectors: floorplan.flameDetectors.filter((d) => !groupedIds.has(d.id)),
      alarmDevices: floorplan.alarmDevices.filter((d) => !groupedIds.has(d.id)),
      signalElements: floorplan.signalElements.filter((d) => !groupedIds.has(d.id)),
      plasticSigns: floorplan.plasticSigns.filter((d) => !groupedIds.has(d.id)),
      gasWarningCenterPlaceholders: floorplan.gasWarningCenterPlaceholders.filter((d) => !groupedIds.has(d.id)),
      transmitterPlaceholders: floorplan.transmitterPlaceholders.filter((d) => !groupedIds.has(d.id)),
      flameDetectorPlaceholders: floorplan.flameDetectorPlaceholders.filter((d) => !groupedIds.has(d.id)),
      alarmDevicePlaceholders: floorplan.alarmDevicePlaceholders.filter((d) => !groupedIds.has(d.id)),
      signalElementPlaceholders: floorplan.signalElementPlaceholders.filter((d) => !groupedIds.has(d.id)),
      plasticSignPlaceholders: floorplan.plasticSignPlaceholders.filter((d) => !groupedIds.has(d.id)),
    };
    this.defaultNotesGroup.sections.push(...this.buildSections(ungroupedSource));

    for (const group of project.groups.filter((g) => g.floorplanIds.includes(floorplan.id))) {
      const refIds = new Set(group.references.filter((r) => r.floorplanId === floorplan.id).map((r) => r.floorplanProductItemId));
      const groupSource: Record<NotesSectionKey, NotesSourceValue> = {
        gasWarningCenters: floorplan.gasWarningCenters.filter((d) => refIds.has(d.id)),
        transmitters: floorplan.transmitters.filter((d) => refIds.has(d.id)),
        flameDetectors: floorplan.flameDetectors.filter((d) => refIds.has(d.id)),
        alarmDevices: floorplan.alarmDevices.filter((d) => refIds.has(d.id)),
        signalElements: floorplan.signalElements.filter((d) => refIds.has(d.id)),
        plasticSigns: floorplan.plasticSigns.filter((d) => refIds.has(d.id)),
        gasWarningCenterPlaceholders: floorplan.gasWarningCenterPlaceholders.filter((d) => refIds.has(d.id)),
        transmitterPlaceholders: floorplan.transmitterPlaceholders.filter((d) => refIds.has(d.id)),
        flameDetectorPlaceholders: floorplan.flameDetectorPlaceholders.filter((d) => refIds.has(d.id)),
        alarmDevicePlaceholders: floorplan.alarmDevicePlaceholders.filter((d) => refIds.has(d.id)),
        signalElementPlaceholders: floorplan.signalElementPlaceholders.filter((d) => refIds.has(d.id)),
        plasticSignPlaceholders: floorplan.plasticSignPlaceholders.filter((d) => refIds.has(d.id)),
      };
      this.groups.push({ name: group.name, sections: this.buildSections(groupSource) });
    }
  }

  private readonly NOTES_SECTION_KEY_TO_PRODUCT_TYPE: Record<NotesSectionKey, ProductTypes> = {
    gasWarningCenters: ProductTypes.GAS_WARNING_CENTER,
    transmitters: ProductTypes.TRANSMITTER,
    flameDetectors: ProductTypes.FLAME_DETECTOR,
    alarmDevices: ProductTypes.ALARM_DEVICE,
    signalElements: ProductTypes.SIGNAL_ELEMENT,
    plasticSigns: ProductTypes.PLASTICSIGN,
    gasWarningCenterPlaceholders: ProductTypes.GAS_WARNING_CENTER_PLACEHOLDER,
    transmitterPlaceholders: ProductTypes.TRANSMITTER_PLACEHOLDER,
    flameDetectorPlaceholders: ProductTypes.FLAME_DETECTOR_PLACEHOLDER,
    alarmDevicePlaceholders: ProductTypes.ALARM_DEVICE_PLACEHOLDER,
    signalElementPlaceholders: ProductTypes.SIGNALELEMENT_PLACEHOLDER,
    plasticSignPlaceholders: ProductTypes.PLASTICSIGN_PLACEHOLDER,
  };
}
